import { useState } from "react";
import { X, Loader2, AlertTriangle, ShieldAlert } from "lucide-react";
import { useAdminOverrideCommitteeDecisionMutation } from "../api/upgradeApi";
import { describeApiError } from "../../../lib/apiError";

type Outcome = "recommended" | "not_recommended" | "approved";

/**
 * The secretariat decides an application at the committee stage itself.
 *
 * An override, and written as one throughout. Admin approve/reject at
 * `under_review` was deliberately removed when the committee flow was built,
 * so this dialog does not pretend to be an ordinary decision: it names the
 * committee being bypassed, demands a reason, and says on each outcome what
 * that outcome actually does to the applicant.
 *
 * Nothing is written to the assigned members' own rows — crediting people with
 * a recommendation they never gave would corrupt the tallies the exports are
 * taken from. See the server's `override()` for the same note.
 */
const OUTCOMES: {
  key: Outcome;
  label: string;
  detail: string;
  tone: string;
  active: string;
}[] = [
  {
    key: "recommended",
    label: "Recommend",
    detail:
      "The committee's own verdict. Marks the application recommended, which is what lets the applicant pay their induction fees.",
    tone: "hover:border-emerald-300",
    active: "border-emerald-500 bg-emerald-50",
  },
  {
    key: "not_recommended",
    label: "Do not recommend",
    detail:
      "Marks the application not recommended. It goes to the committee chairman to finalise.",
    tone: "hover:border-rose-300",
    active: "border-rose-500 bg-rose-50",
  },
  {
    key: "approved",
    label: "Approve outright",
    detail:
      "The chairman's finalise, done here. The applicant is approved without the committee stage — and the induction bundle is only ever payable from “recommended”, so it can no longer be charged.",
    tone: "hover:border-indigo-300",
    active: "border-indigo-500 bg-indigo-50",
  },
];

export default function OverrideCommitteeDecisionModal({
  app,
  onClose,
  onDone,
}: {
  /** The in-cycle row: { id, name, membership_no, status, reviews }. */
  app: any;
  onClose: () => void;
  onDone?: (message: string) => void;
}) {
  const [outcome, setOutcome] = useState<Outcome | "">("");
  const [remark, setRemark] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const [override, { isLoading: saving }] = useAdminOverrideCommitteeDecisionMutation();

  // Recommendations already on the application. The admin is about to decide
  // over the top of them, so they are shown rather than left to be remembered.
  const decided: any[] = (app?.reviews ?? []).filter(
    (r: any) => r.decision === "approved" || r.decision === "rejected",
  );
  const pending: any[] = (app?.reviews ?? []).filter((r: any) => r.decision === "pending");

  const submit = async () => {
    if (!outcome || remark.trim().length < 3) return;
    setErr(null);

    try {
      const res: any = await override({ id: app.id, outcome, remark: remark.trim() }).unwrap();
      onDone?.(res?.message ?? "Override recorded.");
      onClose();
    } catch (e: any) {
      setErr(describeApiError(e) || "That did not work. Please try again.");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 md:p-8">
      <div className="w-full max-w-xl bg-white rounded-[28px] shadow-2xl border border-gray-100 overflow-hidden">
        <div className="flex items-start justify-between px-6 py-5 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-50 border border-amber-200 rounded-2xl flex items-center justify-center">
              <ShieldAlert className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h2 className="text-lg font-black text-gray-900 tracking-tight">
                Decide on behalf of the committee
              </h2>
              <p className="text-xs font-semibold text-gray-500">
                {app?.name || "this applicant"}
                {app?.membership_no ? ` · ${app.membership_no}` : ""} · currently {app?.status || "—"}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-gray-50 text-gray-400">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">
          <div className="flex gap-2 p-3 rounded-2xl bg-amber-50 border border-amber-200 text-[12px] font-semibold text-amber-800">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <div>
              This is an override. The Membership Committee normally decides this stage, and the
              decision is recorded against your name as the secretariat's, not as a committee
              recommendation. Nothing is written to the assigned members' own rows.
            </div>
          </div>

          {/* Where the committee has actually got to. An override made in
              ignorance of two recommendations already on the row is the mistake
              worth designing against. */}
          {decided.length || pending.length ? (
            <div className="text-[11px] font-semibold text-gray-500 leading-relaxed">
              On the committee's rows so far:{" "}
              {decided.length ? (
                <>
                  {decided.map((r: any, i: number) => (
                    <span key={r.id}>
                      {i > 0 ? ", " : ""}
                      <b className={r.decision === "approved" ? "text-emerald-700" : "text-rose-700"}>
                        {r.name} {r.decision === "approved" ? "recommends" : "does not"}
                      </b>
                    </span>
                  ))}
                  {pending.length ? ` · ${pending.length} still to decide` : ""}
                </>
              ) : (
                `nobody has decided yet · ${pending.length} assigned`
              )}
              .
            </div>
          ) : (
            <div className="text-[11px] font-semibold text-gray-500">
              No committee member is assigned to this application.
            </div>
          )}

          <div className="space-y-2">
            <label className="text-[11px] font-black uppercase tracking-widest text-gray-400">
              Decision
            </label>
            {OUTCOMES.map((o) => (
              <button
                key={o.key}
                type="button"
                onClick={() => setOutcome(o.key)}
                className={`w-full text-left px-4 py-3 rounded-2xl border transition ${
                  outcome === o.key ? o.active : `bg-white border-gray-200 ${o.tone}`
                }`}
              >
                <div className="text-sm font-bold text-gray-900">{o.label}</div>
                <div className="mt-0.5 text-[11px] font-semibold text-gray-500 leading-relaxed">
                  {o.detail}
                </div>
              </button>
            ))}
          </div>

          <div>
            <label className="text-[11px] font-black uppercase tracking-widest text-gray-400">
              Reason <span className="text-rose-500">· required</span>
            </label>
            {/* Required by the server too, not only here. A committee
                recommendation carries its own authority; an override has none
                beyond the reason given for it. */}
            <textarea
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
              rows={3}
              placeholder="Why the secretariat is deciding this one — e.g. agreed at the sitting of 12 Oct, minuted; reviewer left the committee mid-cycle."
              className="mt-2 w-full px-4 py-3 border border-gray-200 rounded-2xl text-sm outline-none focus:ring-2 focus:ring-gray-100"
            />
            <p className="mt-1 text-[11px] font-semibold text-gray-400">
              Kept on the application and in its review trail, marked as an override.
            </p>
          </div>

          {err ? (
            <div className="flex gap-2 p-3 rounded-2xl bg-rose-50 border border-rose-200 text-[12px] font-semibold text-rose-700">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <div>{err}</div>
            </div>
          ) : null}
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50/60">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl text-xs font-bold text-gray-500 hover:bg-white"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={!outcome || remark.trim().length < 3 || saving}
            className="inline-flex items-center gap-2 px-5 py-2 rounded-xl bg-amber-600 text-white text-xs font-bold hover:bg-amber-700 disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
            Record override
          </button>
        </div>
      </div>
    </div>
  );
}
